import { deepSignal } from 'deepsignal';
import { isYes } from '@util/common.js';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import Description from '@components/overlays/shared/description.jsx';
import NumberInput from '@components/input/number.input.jsx';
import LinkSetup from '@components/overlays/social/link-setup/link-setup.jsx';
import NumberSliderInput from '@components/input/number-slider.input.jsx';
import { AlertsOverlay } from '@playlive/overlay-core/overlays/alerts';
import { CHARITY } from '@util/const.js';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import CharitySection from '@components/shared/charity-section.jsx';
import SendTestDonation from '@components/overlays/shared/send-test-donation.jsx';
import YesNoButtonInput from '@components/input/yes-no-button.input.jsx';
import HorizontalCustomizeWrapper from '@components/overlays/wrapper/horizonal-customize-wrapper.jsx';
import { useEffect, useState } from 'preact/hooks';
import useCommon from '@hooks/useCommon.js';

import './alert.scss';

const config = deepSignal({
  tiltifyUserID: null,
  tiltifyUsername: null,
  twitchOAuthToken: null,
  twitchUsername: null,
  tiltifyCampaignSlug: null,
  tiltifyCampaignID: null,
  tiltifyPollID: null,
  charityType: CHARITY.TILTIFY,
  incentives: 'no',
  minDonation: 5,
  minAudio: 5,
  sound: 'yes',
  volume: 50,
});

const names = {
  first: ['Steve', 'Paul', 'John'],
  last: ['Smith', 'Test', 'Lastname'],
};

const amounts = ['5', '25', '50', '15', '100'];

const getRand = (arr) => {
  return arr[Math.floor(Math.random() * arr.length)];
};

const getFullName = () => {
  return `${getRand(names.first)} ${getRand(names.last)}`;
};

const getRandomAmount = () => {
  return getRand(amounts);
};

const donation = {
  id: `demo-donation-${new Date().toISOString()}`,
  donor_name: getFullName(),
  completed_at: new Date().toISOString(),
  amount: {
    value: getRandomAmount(),
  },
  reward_id: 'demo-reward',
  reward_claims: [
    {
      id: 'demo-reward',
      reward_id: 'demo-reward',
      quantity: 1,
    },
  ],
};

export default function AlertsOverlayPage() {
  const { setConfigState, overlayURL, overlayInfo } = useCommon({ config });

  const [currentDonation, setCurrentDonation] = useState(donation);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentDonation({
        ...donation,
        id: `demo-donation-${new Date().toISOString()}`,
        donor_name: getFullName(),
        amount: { value: getRandomAmount() },
        completed_at: new Date().toISOString(),
      });
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const reward = {
    id: 'demo-reward',
    name: 'Reward',
    active: true,
    highlighted: false,
    quantity: 1,
    quantity_remaining: 1,
    amount: {
      value: '1',
    },
  };

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col lg:flex-row overflow-x-hidden">
        <div className="example_overlay select-none overflow-x-hidden w-full">
          <ScaledOverlayWrapper allowScaling={false} defaultScale="scale-100">
            <AlertsOverlay
              volume={0}
              minAmount={0}
              donation={currentDonation}
              rewards={[reward]}
              minDonation={0}
              incentivesEnabled={isYes(config.incentives)}
              audioEnabled={false}
              minAudio={0}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper>
          <CustomizeSection title="Alert Settings">
            <div className="mb-6">
              <NumberInput
                name="minDonation"
                value={Math.max(Math.abs(Number(config.minDonation || 0)), 1)}
                placeholder="5"
                onChange={setConfigState}
                isCurrency={true}
                maxLength={4}
                minValue={1}
                maxValue={99999}
                labelText="Minimum Trigger Amount"
                description="Minimum dollar amount required to trigger an alert"
              />

              <NumberInput
                name="minAudio"
                value={Number(config.minAudio || 0)}
                placeholder="5"
                onChange={setConfigState}
                isCurrency={true}
                maxLength={4}
                minValue={0}
                className="mb-5"
                labelText="Minimum Sound Amount"
                description="Minimum dollar amount required to trigger an alert with sound"
              />

              <div className="flex flex-col md:flex-row">
                <YesNoButtonInput
                  mode="checkbox"
                  className="md:mr-4 mb-2 md:mb-0 flex-1"
                  onChange={setConfigState}
                  currentValue={config.sound}
                  name="sound"
                  label="Alert sound enabled"
                />

                <YesNoButtonInput
                  mode="checkbox"
                  onChange={setConfigState}
                  currentValue={config.incentives}
                  name="incentives"
                  label="Display rewards"
                  className="flex-1"
                />
              </div>

              <div className="mt-6">
                <NumberSliderInput
                  fieldName="volume"
                  maxValue={100}
                  minValue={1}
                  disabled={config.sound === 'no'}
                  step={1}
                  labelText="Volume Level"
                  onChange={setConfigState}
                  value={config.volume}
                  suffix="%"
                />
              </div>
            </div>
          </CustomizeSection>

          <CharitySection
            onChange={setConfigState}
            charityType={config.charityType}
          />
        </HorizontalCustomizeWrapper>
      </div>

      <Description title="Hidden Donation Alerts">
        <p>
          The St. Jude Play Live alerts include hidden donation amounts that are
          triggered for specific donation amounts. Encourage your donors to seek
          out these alerts during your fundraising campaign or by including them
          as global rewards on Tiltify. The hidden alert amounts include:
        </p>
        <div className="flex flex-row items-center justify-evenly pt-6 gtg-alerts">
          <ul>
            <li className="nomore">
              <span>
                <strong>$5</strong> No More Chemo Party
              </span>
            </li>
            <li className="new-toys">
              <span>
                <strong>$10</strong> A new toy for St. Jude
              </span>
            </li>
            <li className="food">
              <span>
                <strong>$25</strong> One day of meals for a patient
              </span>
            </li>
            <li className="art">
              <span>
                <strong>$50</strong> Art supplies for St. Jude
              </span>
            </li>
            <li className="wagon">
              <span>
                <strong>$100</strong> A red wagon for St. Jude
              </span>
            </li>
          </ul>
          <ul>
            <li className="food">
              <span>
                <strong>$300</strong> Groceries for a family for two weeks
              </span>
            </li>
            <li className="doctor">
              <span>
                <strong>$500</strong> Two days of oxygen for a patient
              </span>
            </li>
            <li className="travel">
              <span>
                <strong>$1000</strong> Airfare for patients and families
              </span>
            </li>
            <li className="chemo">
              <span>
                <strong>$1500</strong> One day of chemotherapy for a patient
              </span>
            </li>
          </ul>
        </div>
      </Description>

      <LinkSetup
        constrainWidth={false}
        childrenSlot="stepThree"
        childrenSlotPosition="after"
        charityType={config.charityType}
        obsDimensions={overlayInfo.obsDimensions}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
      >
        <SendTestDonation
          sendButtonText="Send Alert"
          instructions="After you have added your browser source link to your streaming software, enter an amount, and then send a test donation"
        />
      </LinkSetup>
    </div>
  );
}
